import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import {
  Eye,
  EyeOff,
  LogIn,
  Building2,
  ArrowRight,
  ChevronLeft,
  ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantSession } from "@/contexts/TenantSessionContext";
import { getTenantSlug, apiVerify2FA, apiGetSsoStatus, API_BASE } from "@/lib/api";
import { useTranslation } from "react-i18next";

export default function MobileLogin() {
  const { t } = useTranslation();
  const { login, completeTwoFactorLogin } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tenantSlug, switchTenant } = useTenantSession();

  const isSubdomainTenant = (() => {
    try {
      const hostname = window.location.hostname;
      const siteDomain =
        (import.meta.env.VITE_SITE_DOMAIN as string | undefined) ||
        "portal.besthr.app";
      const parts = hostname.split(".");
      return (
        hostname.endsWith(`.${siteDomain}`) &&
        parts.length >= 3 &&
        !["www", "api", "portal"].includes(parts[0])
      );
    } catch {
      return false;
    }
  })();

  const resolvedTenant = getTenantSlug();
  const [step, setStep] = useState<"tenant" | "login" | "2fa">(
    resolvedTenant !== "public" ? "login" : "tenant"
  );
  const [tenantInput, setTenantInput] = useState(
    resolvedTenant !== "public" ? resolvedTenant : ""
  );

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [tempToken, setTempToken] = useState("");
  const [otpCode, setOtpCode] = useState("");

  // Optimistic init for subdomain tenants — fetch errors are silent on mobile
  const [ssoEnabled, setSsoEnabled] = useState(isSubdomainTenant);

  useEffect(() => {
    const ssoErr = searchParams.get("ssoError");
    if (ssoErr) setError(ssoErr);
  }, [searchParams]);

  useEffect(() => {
    if (step !== "login") return;
    const slug = getTenantSlug();
    if (slug === "public") return;
    apiGetSsoStatus().then((r) => setSsoEnabled(r.enabled)).catch(() => {});
  }, [step]);

  function handleContinue(e: React.FormEvent) {
    e.preventDefault();
    const slug = tenantInput.trim().toLowerCase();
    if (!slug) return;
    const siteDomain = (import.meta.env.VITE_SITE_DOMAIN as string | undefined) || "portal.besthr.app";
    if (window.location.hostname === siteDomain) {
      window.location.href = `https://${slug}.${siteDomain}/mobile/login`;
      return;
    }
    switchTenant(slug);
    setStep("login");
  }

  function handleSwitchCompany() {
    setStep("tenant");
    setEmail("");
    setPassword("");
    setError("");
    setTempToken("");
    setOtpCode("");
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    const result = await login(email, password);
    setIsLoading(false);
    if (result.success && result.requires2FA && result.tempToken) {
      setTempToken(result.tempToken);
      setStep("2fa");
    } else if (result.success) {
      navigate("/mobile");
    } else {
      setError(result.error ? t(result.error) : "Login failed");
    }
  };

  const handle2FASubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (otpCode.length !== 6) return;
    setError("");
    setIsLoading(true);
    try {
      const result = await apiVerify2FA(tempToken, otpCode);
      completeTwoFactorLogin(result.user, result.token, result.tenantSlug);
      navigate("/mobile");
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : "Invalid code. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  const pageClass =
    "min-h-dvh bg-gradient-to-br from-background via-background to-accent/40 flex flex-col items-center justify-center p-6";

  // ── Step 1: Tenant ────────────────────────────────────────────────────────
  if (step === "tenant") {
    return (
      <div className={pageClass}>
        <div className="w-full max-w-sm">
          <div className="bg-card border border-border rounded-2xl shadow-elevated p-8">
            <div className="text-center mb-8">
              <img src="/besthr.png" alt="BestHR" className="h-14 mx-auto mb-5" />
              <h1 className="text-2xl font-bold text-foreground">
                {t("login.welcomeTitle")}
              </h1>
              <p className="text-muted-foreground mt-1.5 text-sm">
                {t("login.welcomeSubtitle")}
              </p>
            </div>

            <form onSubmit={handleContinue} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="tenant" className="text-sm font-medium">
                  {t("login.companyIdLabel")}
                </Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                  <Input
                    id="tenant"
                    type="text"
                    placeholder={t("login.companyIdPlaceholder")}
                    value={tenantInput}
                    onChange={(e) => setTenantInput(e.target.value)}
                    className="h-12 pl-9 font-mono text-sm"
                    autoComplete="off"
                    spellCheck={false}
                    autoFocus
                  />
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {t("login.companyIdHint")}
                </p>
              </div>
              <Button
                type="submit"
                className="w-full h-12 gradient-brand text-primary-foreground font-medium"
                disabled={!tenantInput.trim()}
              >
                {t("login.continue")}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </form>
          </div>
          <p className="text-center text-xs text-muted-foreground mt-6">
            {t("login.copyright")}
          </p>
        </div>
      </div>
    );
  }

  // ── Step 3: 2FA ───────────────────────────────────────────────────────────
  if (step === "2fa") {
    return (
      <div className={pageClass}>
        <div className="w-full max-w-sm">
          <div className="bg-card border border-border rounded-2xl shadow-elevated p-8">
            <div className="text-center mb-8">
              <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 mb-4">
                <ShieldCheck className="h-7 w-7 text-primary" />
              </div>
              <h1 className="text-2xl font-bold text-foreground">
                Two-Factor Authentication
              </h1>
              <p className="text-muted-foreground mt-1.5 text-sm">
                Enter the 6-digit code from your authenticator app.
              </p>
            </div>

            <form onSubmit={handle2FASubmit} className="space-y-6">
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={otpCode}
                  onChange={setOtpCode}
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              {error && (
                <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg text-center">
                  {error}
                </div>
              )}
              <Button
                type="submit"
                className="w-full h-12 gradient-brand text-primary-foreground font-medium"
                disabled={isLoading || otpCode.length !== 6}
              >
                {isLoading ? (
                  <>
                    <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Verifying…
                  </>
                ) : (
                  <>
                    <ShieldCheck className="h-4 w-4 mr-2" />
                    Verify Code
                  </>
                )}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => { setStep("login"); setOtpCode(""); setError(""); }}
                className="text-xs text-muted-foreground hover:text-primary transition-colors"
              >
                ← Back to login
              </button>
            </div>
          </div>
          <p className="text-center text-xs text-muted-foreground mt-6">
            {t("login.copyright")}
          </p>
        </div>
      </div>
    );
  }

  // ── Step 2: Credentials ───────────────────────────────────────────────────
  return (
    <div className={pageClass}>
      <div className="w-full max-w-sm">
        <div className="bg-card border border-border rounded-2xl shadow-elevated p-8">
          <div className="text-center mb-6">
            <img src="/besthr.png" alt="BestHR" className="h-10 mx-auto mb-4" />
            {isSubdomainTenant ? (
              <span className="inline-flex items-center gap-1.5 text-xs bg-primary/10 text-primary border border-primary/20 rounded-full px-3 py-1">
                <Building2 className="h-3 w-3" />
                <span className="font-mono font-medium">{tenantSlug}</span>
              </span>
            ) : (
              <button
                type="button"
                onClick={handleSwitchCompany}
                className="inline-flex items-center gap-1.5 text-xs bg-primary/10 text-primary border border-primary/20 rounded-full px-3 py-1 hover:bg-primary/15 transition-colors"
              >
                <Building2 className="h-3 w-3" />
                <span className="font-mono font-medium">{tenantSlug}</span>
                <ChevronLeft className="h-3 w-3 rotate-180 opacity-50" />
              </button>
            )}
          </div>

          <div className="mb-6 text-center">
            <h1 className="text-2xl font-bold text-foreground">{t("login.title")}</h1>
            <p className="text-muted-foreground mt-1 text-sm">{t("login.subtitle")}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                {t("login.emailLabel")}
              </Label>
              <Input
                id="email"
                type="email"
                placeholder={t("login.emailPlaceholder")}
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(""); }}
                required
                autoComplete="email"
                className="h-12"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium">
                  {t("login.passwordLabel")}
                </Label>
                <button
                  type="button"
                  onClick={() => navigate("/forgot-password")}
                  className="text-xs text-primary hover:underline"
                >
                  {t("login.forgotPassword")}
                </button>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder={t("login.passwordPlaceholder")}
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setError(""); }}
                  required
                  autoComplete="current-password"
                  className="h-12 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-12 gradient-brand text-primary-foreground font-medium"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  {t("login.signingIn")}
                </>
              ) : (
                <>
                  <LogIn className="h-4 w-4 mr-2" />
                  {t("login.signInButton")}
                </>
              )}
            </Button>
          </form>

          {ssoEnabled && (
            <>
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-2 text-muted-foreground">or</span>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                className="w-full h-12"
                onClick={() => {
                  const slug = getTenantSlug();
                  window.location.href = `${API_BASE}/api/sso/login?_t=${encodeURIComponent(slug)}`;
                }}
              >
                <ShieldCheck className="h-4 w-4 mr-2" />
                Sign in with SSO
              </Button>
            </>
          )}

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={handleSwitchCompany}
              className="text-xs text-muted-foreground hover:text-primary transition-colors"
            >
              {t("login.switchCompany")}
            </button>
          </div>
        </div>
        <p className="text-center text-xs text-muted-foreground mt-6">
          {t("login.copyright")}
        </p>
      </div>
    </div>
  );
}
